import { GoogleLogin } from "@react-oauth/google";
import { useFormik } from "formik";
import { ArrowRight } from "lucide-react";
import React, { useContext, useState } from "react";
import ReactInputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import logo from "@/assets/altLogo.svg";
import image2 from "@/assets/fuundo_ondas.png";
import image1 from "@/assets/fuundo_ondas (1).png";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthContext } from "@/providers/AuthProvider";
import { getDemoAssistantData } from "@/utils/demoAssistant";
import { maskPhone } from "@/utils/masks";

import "./SignUp.css";
import "../Login/Login.css";
import "@/styles/auth-theme.css";

const SignUp = () => {
  const navigate = useNavigate();
  const { signUp, signInWithGoogle } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  // Se a pessoa já deixou email/telefone no gate do demo, pré-preenche pra não
  // precisar digitar de novo no cadastro.
  const demoData = getDemoAssistantData();

  const form = useFormik({
    initialValues: {
      name: "",
      email: demoData?.email || "",
      // O demo guarda o telefone em dígitos crus; aqui ele volta pra máscara,
      // que é o formato que este form usa e envia ao backend. maskPhone também
      // normaliza valores mascarados antigos já salvos no localStorage.
      phone: maskPhone(demoData?.phone || ""),
      password: "",
      passwordConfirmation: "",
    },
    validationSchema: yup.object({
      name: yup.string().required("O campo nome é obrigatório!"),
      email: yup
        .string()
        .email("Formato de email inválido.")
        .required("O campo e-mail é obrigatório!"),
      phone: yup
        .string()
        .required("Telefone de contato é obrigatório!")
        // Conta dígitos em vez de caracteres: contar caracteres assumia que o
        // valor sempre chega mascarado, o que quebra qualquer pré-preenchimento
        // vindo em dígitos crus.
        .test("phone-valid", "Telefone inválido!", (value) => {
          const digits = (value || "").replace(/\D/g, "");
          return digits.length >= 10 && digits.length <= 11;
        }),
      password: yup
        .string()
        .min(8, "Senha deve conter no mínimo 8 caracteres.")
        .required("O campo senha é obrigatório!"),
      passwordConfirmation: yup
        .string()
        .oneOf([yup.ref("password")], "As senhas devem corresponder.")
        .required("O campo de confirmação de senha é obrigatório!"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const response = await signUp({ values });
        if (response === "success") {
          // Navigate to ThankYou page - user context is NOT set yet to avoid race condition
          // ThankYou page will call loadUserFromStorage() before redirecting to dashboard
          navigate("/obrigado", {
            state: {
              partnerName: values.name,
              isNewRegistration: true,
            },
          });
        }
      } catch (error) {
        // Handle error if necessary
      } finally {
        setLoading(false);
      }
    },
  });

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      if (credentialResponse?.credential) {
        const result = await signInWithGoogle(credentialResponse.credential);
        if (result?.isNewRegistration) {
          navigate("/obrigado", {
            state: { partnerName: result.partnerName, isNewRegistration: true },
          });
        }
        // usuários existentes: o PublicRoute cuida do redirect (inclui o fluxo de demo)
      }
    } catch {
      // erro já tratado no provider
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => setLoading(false);

  const renderInputField = (label, name, type = "text", mask = false, placeholder = "") => (
    <div className="space-y-1">
      <Label htmlFor={name} className="auth-label">
        {label}
      </Label>
      {mask ? (
        <ReactInputMask
          mask={
            form.values[name].replace(/\s/g, "").length > 13
              ? "(99) 9 9999-9999"
              : "(99) 9999-99999"
          }
          maskChar={""}
          name={name}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          value={form.values[name]}
        >
          {(inputProps) => {
            const mergedProps = {
              ...inputProps,
              id: name,
            };
            return (
              <Input
                {...mergedProps}
                type="text"
                className="auth-input"
                placeholder={placeholder}
              />
            );
          }}
        </ReactInputMask>
      ) : (
        <Input
          id={name}
          name={name}
          type={type}
          placeholder={placeholder}
          value={form.values[name]}
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          className="auth-input"
          autoComplete="new-password"
        />
      )}
      {form.touched[name] && form.errors[name] && (
        <div className="auth-error">{form.errors[name]}</div>
      )}
    </div>
  );

  return (
    <div className="auth-container">
      <img src={image1} className="auth-bg-image auth-bg-image-1" alt="Background decoration" />
      <img src={image2} className="auth-bg-image auth-bg-image-2" alt="Background decoration" />

      <div className="auth-content">
        <div className="auth-logo-container">
          <a href="/">
            <img src={logo} alt="SacGPT Logo" className="auth-logo" />
          </a>
        </div>

        <Card className="auth-card">
          <CardContent>
            <div className="login-welcome-header">
              <h1 className="login-welcome-title">Crie sua conta</h1>
              <p className="login-welcome-subtitle">Comece em segundos</p>
            </div>

            <div className="google-login-container">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                theme="filled_blue"
                size="large"
                text="signup_with"
                shape="pill"
                width="320"
                locale="pt-BR"
              />
            </div>

            <div className="login-choice-divider">
              <span>ou</span>
            </div>

            <form onSubmit={form.handleSubmit} className="space-y-5">
              {renderInputField("Nome", "name", "text", false, "Digite seu nome completo")}
              {renderInputField("E-mail", "email", "email", false, "Digite seu e-mail")}
              {renderInputField("Telefone", "phone", "text", true, "Digite seu telefone")}
              <div className="grid grid-cols-2 gap-3">
                {renderInputField("Senha", "password", "password", false, "Senha")}
                {renderInputField(
                  "Confirme",
                  "passwordConfirmation",
                  "password",
                  false,
                  "Confirme",
                )}
              </div>

              <Button type="submit" disabled={loading} className="auth-button-primary w-full">
                {loading ? (
                  <>
                    <div className="auth-loading-spinner" />
                    Carregando...
                  </>
                ) : (
                  <>
                    <span>Cadastrar</span>
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Button variant="link" onClick={() => navigate("/")} className="auth-link text-sm">
                Já tem uma conta? <span className="underline font-semibold">Faça login</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <footer className="auth-footer">sacgpt.com.br © 2025 - Todos os Direitos Reservados</footer>
    </div>
  );
};

export default SignUp;
